import React from 'react';
import { PartyPopper, CheckCircle2, X, Sparkles, TrendingUp } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { SUPPORT_ROLES } from '../../data/supportRoles';
import { getBonusImpactExplainer } from '../../utils/bonusImpactExplainer';
import { useTranslation } from '../../i18n';

/**
 * 02/06/2026 — Celebration popup that fires the first time a support role
 * is hired. Shows a "Congratulations" banner + the concrete list of
 * bonuses the player now has access to, pulled from `r.bonuses[].label`.
 *
 * Driven by the `justHiredSupport` state key set inside `hireSupport`.
 * Clears that key on close so it only shows once per hire.
 */
export default function SupportHireCelebration() {
  const { t } = useTranslation();
  const justHired = useGameStore(s => s.justHiredSupport);
  const clear = useGameStore(s => s.clearJustHiredSupport);
  if (!justHired) return null;

  const role = SUPPORT_ROLES.find(r => r.id === justHired.roleId);
  if (!role) return null;

  const bonuses = (role.bonuses || []).map(b => b.label).filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={clear}
      data-testid="support-hire-celebration"
    >
      <div
        className="bg-gradient-to-br from-emerald-900/90 via-ink-800 to-ink-800 border-2 border-emerald-500/50 rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="text-center relative">
          <button
            onClick={clear}
            className="absolute top-0 right-0 p-1 text-slate-400 hover:text-foreground"
            data-testid="celebration-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border-2 border-emerald-500/40 mb-2">
            <PartyPopper className="w-7 h-7 text-emerald-300" />
          </div>
          <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-mono font-bold flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3" /> {t('support_celebration_kicker')}
          </p>
          <h2 className="font-display text-xl font-black text-foreground mt-1">
            {t('support_celebration_title', { name: role.name })}
          </h2>
          <p className="text-xs text-slate-400 mt-1">{role.role}</p>
        </div>

        {/* Bonuses unlocked */}
        <div
          className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4"
          data-testid="celebration-bonuses"
        >
          <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-mono font-bold mb-2">
            {t('support_celebration_subtitle')}
          </p>
          {bonuses.length > 0 ? (
            <ul className="space-y-2">
              {bonuses.map((b, i) => (
                <li
                  key={`bonus-${i}-${String(b || '').slice(0, 24)}`}
                  className="flex items-start gap-2 text-sm text-slate-100 leading-snug"
                  data-testid={`celebration-bonus-${i}`}
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          ) : role.background ? (
            <p className="text-sm text-slate-200 leading-relaxed">{role.background}</p>
          ) : (
            <p className="text-sm text-slate-400 italic">
              {t('support_celebration_pending_bonuses')}
            </p>
          )}
        </div>

        {/* Impact métier concret — 03/06/2026 */}
        {(role.bonuses || []).length > 0 && (
          <div
            className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4"
            data-testid="celebration-impact"
          >
            <p className="text-[10px] uppercase tracking-wider text-amber-300 font-mono font-bold mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" /> Impact métier concret
            </p>
            <ul className="space-y-2.5">
              {(role.bonuses || []).map((b, i) => {
                const impact = getBonusImpactExplainer(b);
                return (
                  <li
                    key={`impact-${i}-${(b?.label || b?.type || '').slice(0, 24)}`}
                    className="text-xs text-slate-200 leading-relaxed"
                    data-testid={`celebration-impact-${i}`}
                  >
                    <span className="font-bold text-amber-200">{impact.kpi}</span>
                    <span className="text-slate-400"> — </span>
                    <span>{impact.detail}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* CTA */}
        <button
          onClick={clear}
          className="w-full px-4 py-2.5 rounded-lg bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 transition-colors"
          data-testid="celebration-continue"
        >
          {t('support_celebration_continue')}
        </button>
      </div>
    </div>
  );
}
